import axios from "axios";
import { get, intersectionWith, isEqual } from "lodash";
import {
  AD_USER,
  ERROR_TYPES,
  TIME_ZONE,
  TIME_ZONE_ABBR,
} from "../consts/Common";
import { TEAM, TEAM_USER } from "../consts/DBFields";
import Configs, { get as getConfiguration } from "../Configurations";
import * as XLSX from "xlsx";

const jwt = require("jsonwebtoken");

export const getIntValue = (input) => {
  const intValue = parseInt(input);
  if (!isNaN(intValue)) {
    return intValue;
  }
  return null;
};

let expiryInHrs = getIntValue(Configs.REACT_APP_ACCESS_KEY_EXPIRY_HRS);
expiryInHrs = expiryInHrs ? expiryInHrs : 8; //defaulting to 8 hours if environment variable is not configured

export function getJSONWebToken() {
  const exp = new Date().setTime(
    new Date().getTime() + expiryInHrs * 60 * 60 * 1000
  );
  const accessKeyId = getConfiguration("REACT_APP_ACCESS_KEY_ID");
  const secretAccessKey = getConfiguration("REACT_APP_SECRET_ACCESS_KEY");
  let token = "";
  if (accessKeyId && secretAccessKey) {
    token = jwt.sign(
      {
        kid: accessKeyId,
        exp: exp,
      },
      secretAccessKey
    );
  }
  return token;
}
export function getRequestConfig(url, httpMethod, data, params) {
  return {
    url: url,
    method: httpMethod,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      jwt: getJSONWebToken(),
    },
    data: data,
    params: params,
  };
}

export function validateAndGetMessage(response) {
  if (response?.data?.type === "success") {
    return response.data.message;
  } else if (response?.status === 204) {
    // teams without campaigns return 204 no data, but app shouldn't go into error state
    return;
  } else {
    const err = new Error("not a success response");
    err.response = response; // preserve so withRetry can check err.response.status
    throw err;
  }
}

export async function getResponse(url, httpMethod, data, params) {
  const config = getRequestConfig(url, httpMethod, data, params);
  let response = await axios(config);
  if (params && params.requestType === "getMessageDetails") {
    return response;
  }
  return validateAndGetMessage(response);
}

// WCNP Migration Change - This function won't be used anymore
export function getEnvVariable(key) {
  return (
    (window.customInjection && window.customInjection[key]) ||
    window.env[key] ||
    undefined
  );
}

//generic error handling function to be shared, for all scenarios that don't warrant a full screen error page
export function handleError(error, alertMsg) {
  console.error(`alert message: ${alertMsg}`, error);
}

export function getErrorType(error) {
  if (error?.status === 401) {
    return ERROR_TYPES.TOKEN_INVALID;
  } else if (error.message === ERROR_TYPES.NETWORK.message) {
    return ERROR_TYPES.NETWORK;
  } else {
    return ERROR_TYPES.DEFAULT;
  }
}

// return a timestamp in "Apr 19, 2025, 8:00" format (short month, no AM/PM)
export const formatDateShort = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    
    // Format: "Apr 19, 2025, 8:00"
    const formattedDate = date.toLocaleString("en-US", {
      timeZone: TIME_ZONE,
      year: "numeric",
      month: "short", // Apr, May, etc.
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false, // 24-hour format to avoid AM/PM
    });
    
    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

// return a timestamp in MM/DD/YYYY 00:00:00 format
export const formatDate = (timestamp, includeTime = true, includeTz, includeSeconds= false) => {
  if (timestamp) {
    const date = new Date(timestamp);
    let formattedDate = `${date.toLocaleString("en-US", {
      timeZone: TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })}`;
    if (includeTime) {
      return `${formattedDate}, ${date.toLocaleTimeString("en-US", {
        timeZone: TIME_ZONE,
        hour: "numeric",
        minute: "2-digit",
        second: includeSeconds ? "2-digit" : undefined,
        hour12: true,
      })}${includeTz ? ` ${TIME_ZONE_ABBR}` : ""}`;
    } else {
      return formattedDate;
    }
  } else {
    return null;
  }
};

export const formatRecipient = (recipient) => {
  if (!recipient || typeof recipient === "string") return recipient;
  else {
    const to = (recipient.to || []).join(", ");
    const cc = (recipient.cc || []).join(", ");
    return to + `${cc.length ? " Cc: " + cc : ""}`;
  }
};

//func to format numbers with commas
export const numberWithCommas = (x) => {
  if (x !== null && x !== undefined) {
    // Use a safer regex for inserting commas in numbers
    let returnVar = x.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    return returnVar;
  } else {
    return "N/A";
  }
};

export const getNameFromUpn = (upn, selectedTeam, teamUsers, abbreviateUpn) => {
  if (upn) {
    const users = teamUsers?.[selectedTeam?.[TEAM.ID]] || [];
    let match = users.find(
      (user) => user[TEAM_USER.UPN].toLowerCase() === upn.toLowerCase()
    );
    if (match) {
      match = match[TEAM_USER.NAME] || match[TEAM_USER.EMAIL];
    }
    if (!match) {
      return abbreviateUpn ? upn.split("@")[0] : upn;
    } else {
      return match;
    }
  }
  return;
};

const upnInLowerCase = (val) => get(val, AD_USER.UPN, "").toLowerCase();

export const intersectUsers = (array1 = [], array2 = []) => {
  return intersectionWith(array1, array2, (value1, value2) =>
    isEqual(upnInLowerCase(value1), upnInLowerCase(value2))
  );
};

const ascendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return 1;
  }
  if (b[orderBy] > a[orderBy]) {
    return -1;
  }
  return 0;
};

const getComparator = (orderBy, order) => {
  return order === "asc"
    ? (a, b) => ascendingComparator(a, b, orderBy)
    : (a, b) => -ascendingComparator(a, b, orderBy);
};

export const stableSort = (array, orderBy, order = "asc") => {
  const comparator = getComparator(orderBy, order);
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export const exportToExcel = (records, fileName = "export.xlsx", options) => {
  const workSheet = XLSX.utils.json_to_sheet(records, options);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, workSheet, "Sheet1");
  XLSX.writeFile(wb, fileName);
};

export const convertToCapitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Rounds a date value down to the start of the hour (strips minutes, seconds, ms).
 * Accepts a Date object or any string/value parseable by new Date().
 * Always returns an ISO 8601 string.
 *
 * Use before passing dates to enterprise stats APIs — the backend caches responses
 * per hour, so sub-hour precision prevents cache hits across long date ranges.
 *
 * e.g. "2026-05-05T14:37:22.456Z" → "2026-05-05T14:00:00.000Z"
 */
export const roundToHour = (date) => {
  if (!date) return date;
  const d = new Date(date);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
};

/**
 * Rounds a date down to the start of the UTC day (midnight).
 * Used for enterprise/admin stats which span multi-year ranges — sub-day
 * precision doesn't improve granularity but hurts backend cache hit rate.
 * e.g. "2026-05-05T14:37:22.456Z" → "2026-05-05T00:00:00.000Z"
 */
export const roundToDay = (date) => {
  if (!date) return date;
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
};

/**
 * Retries an async function when it fails with a 521 (Web Server Down) error or a
 * Network Error (which includes CORS-blocked 521s — when the server returns a 521
 * without CORS headers the browser blocks the response and Axios only sees "Network Error"
 * with no response object). All other error codes throw immediately without retrying,
 * preserving fast failure for auth / validation errors.
 *
 * @param {Function}  fn          - Async function to call and possibly retry.
 * @param {number[]}  retryDelays - Delays in ms before each successive retry.
 *   Array length sets the maximum retry count.
 *   Default [5000, 10000, 15000] → up to 3 retries, waiting 5 s → 10 s → 15 s.
 *
 * Timeline with defaults:
 *   attempt 1 → fail 521/NetworkError → wait 5 s
 *   attempt 2 → fail 521/NetworkError → wait 10 s
 *   attempt 3 → fail 521/NetworkError → wait 15 s
 *   attempt 4 → fail                  → throw
 */
export const withRetry = async (fn, retryDelays = [5000, 10000, 15000]) => {
  for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
    try {
      return await fn();
    } catch (error) {
      // error?.response?.status — standard AxiosError shape
      // error?.status — fallback when the Axios success interceptor rejects with the raw
      //   response object (Promise.reject(response)) instead of a proper Error instance
      const status = error?.response?.status ?? error?.status;
      // When a 521 is returned WITHOUT the Access-Control-Allow-Origin header the browser
      // enforces CORS and Axios never sees the HTTP status — it only gets a Network Error
      // with error.response = undefined. We treat that as a retryable condition since the
      // underlying cause (server overloaded / Cloudflare 521) is the same.
      const isNetworkError = !error?.response && error?.message === "Network Error";
      const shouldRetry = status === 521 || isNetworkError;
      if (attempt === retryDelays.length || !shouldRetry) throw error;
      await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
    }
  }
};